import * as Dialog from '@radix-ui/react-dialog';
import type React from 'react';

import { IconButton } from '~/components/IconButton';
import { cn } from '~/lib/cn';

interface ModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  disableClose?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  description?: string;
}

export function Modal({
  isOpen,
  onOpenChange,
  title,
  disableClose,
  children,
  footer,
  description,
}: ModalProps) {
  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed',
            'inset-0',
            'bg-default',
            'bg-opacity-[0.8]',
            'z-30',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed',
            'left-1/2',
            'top-1/2',
            '-translate-x-1/2',
            '-translate-y-1/2',
            'flex',
            'flex-col',
            'bg-level-01',
            'rounded-sm',
            'w-[42rem]',
            'max-h-[90dvh]',
            'z-40',
          )}
        >
          <Dialog.Title
            className={cn(
              'flex',
              'items-center',
              'justify-between',
              'p-xl',
              'pb-md',
              'gap-md',
              'text-strong',
              'text-body-lg',
            )}
          >
            {title}
            <Dialog.Close asChild>
              <IconButton
                iconType="xmark"
                aria-label="close"
                disabled={disableClose}
              />
            </Dialog.Close>
          </Dialog.Title>

          <div
            className={cn(
              'overflow-auto break-words px-xl pb-lg text-body-sm text-strong',
              {
                'pb-0': footer,
              },
            )}
          >
            {children}
            <Dialog.Description>{description}</Dialog.Description>
          </div>

          {footer ? (
            <footer className={cn('flex', 'justify-end', 'gap-lg', 'p-xl')}>
              {footer}
            </footer>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

Modal.displayName = 'Modal';

Modal.Close = Dialog.Close;
